import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../common/layouts/rowLayout/RowLayout';
import { IChartPoint, IPrecisionRecall, ITrainingMatrix, ITrainingResult } from '../../jobMetrics.model';
import { IValidJobType, JOB_STATUS, JOB_TYPE } from '../../jobs.model';
import { IJobThreshold } from '../../jobThreshold.store';
import { ConfusionMatrix } from './contents/ConfusionMatrix';
import { LossMetrics } from './contents/LossMetrics';
import { MetricsBase } from './contents/MetricsBase';
import { PrecisionRecall } from './contents/PrecisionRecall';
import { ResultsTable } from './contents/ResultsTable';

interface IProps {
  jobType: IValidJobType;
  loss?: IChartPoint[];
  validationAccuracy?: IChartPoint[];
  trainingAccuracy?: IChartPoint[];
  precisionRecall: IPrecisionRecall | null;
  results: ITrainingResult[];
  matrix: ITrainingMatrix;
  recalculateMetrics?: (min: number, max: number) => void;
  threshold?: IJobThreshold | null;
  status: JOB_STATUS;
}

export const FeatureDetection = observer((props: IProps): ReactElement => {
  const { jobType, loss, matrix, precisionRecall, results, recalculateMetrics, threshold, status, validationAccuracy, trainingAccuracy } = props;

  const lossMetrics = useMemo(
    () =>
      loss &&
      validationAccuracy &&
      trainingAccuracy && (
        <LossMetrics
          loss={loss}
          validationAccuracy={validationAccuracy}
          trainingAccuracy={trainingAccuracy}
          status={status}
        />
      ),
    [loss, validationAccuracy, trainingAccuracy]
  );

  return (
    <>
      {jobType === JOB_TYPE.TRAINING && lossMetrics}
      <MetricsBase
        jobType={jobType}
        recalculateMetrics={recalculateMetrics}
        threshold={threshold}
        status={status}
      >
        <RowLayout
          size='md'
          className='metrics-row-base'
        >
          <ColumnLayout size='md'>
            {!!precisionRecall && (
              <PrecisionRecall
                jobType={jobType}
                precisionRecall={precisionRecall}
              />
            )}
          </ColumnLayout>
          <ColumnLayout size='md'>
            <ConfusionMatrix
              matrix={matrix}
              jobType={jobType}
            />
            <ResultsTable
              results={results}
              jobType={jobType}
            />
          </ColumnLayout>
        </RowLayout>
      </MetricsBase>
    </>
  );
});
